import { fetchRenderedHtml } from '@tinycld/core/file-viewer/fetch-rendered-html'
import { captureException, handleMutationErrorsWithForm } from '@tinycld/core/lib/errors'
import { useMutation } from '@tinycld/core/lib/mutations'
import { renderPrintEnvelope } from '@tinycld/core/lib/print/render-print-envelope'
import { Dialog } from '@tinycld/core/ui/dialog'
import { FormErrorSummary, useForm, zodResolver } from '@tinycld/core/ui/form'
import { useMemo } from 'react'
import type * as Y from 'yjs'
import { useYSheets } from '../hooks/use-y-sheets'
import { handlePrint } from '../lib/print/handle-print'
import { buildPrintCss } from '../lib/print/print-css'
import { DEFAULT_PRINT_CONFIG, type PrintConfig, printConfigSchema } from '../lib/print/types'
import { columnLabel } from '../lib/workbook-types'
import { PrintLayoutFields } from './print/PrintLayoutFields'
import { PrintPageFields } from './print/PrintPageFields'
import { PrintScopeFields } from './print/PrintScopeFields'

// PrintSelection mirrors the shape the grid hands to the dialog: a
// sheet ID + an inclusive 1-based rectangle. `null` means "no active
// selection — fall back to the used range". Re-declared locally
// because the old snapshot.ts module that owned this type has been
// retired in favor of the server render path.
export interface PrintSelection {
    sheetId: string
    rect: { startRow: number; startCol: number; endRow: number; endCol: number }
}

export interface PrintDialogProps {
    isOpen: boolean
    onClose: () => void
    doc: Y.Doc
    driveItemId: string
    currentSheetId: string
    currentSelection: PrintSelection | null
}

// rectToA1 stringifies a selection rect into the A1 form the server
// render endpoint accepts.
function rectToA1(rect: PrintSelection['rect']): string {
    const start = `${columnLabel(rect.startCol)}${rect.startRow}`
    const end = `${columnLabel(rect.endCol)}${rect.endRow}`
    return start === end ? start : `${start}:${end}`
}

interface SheetMeta {
    id: string
    name: string
}

// resolveTargets translates the user's sheet-scope choice into the
// concrete (sheetName, optional A1 range) pairs we send to the
// render endpoint. The endpoint renders one sheet per call, so a
// multi-sheet scope becomes multiple calls whose fragments are
// concatenated before being wrapped in the print envelope.
function resolveTargets(
    config: PrintConfig,
    sheets: SheetMeta[],
    currentSheetId: string,
    currentSelection: PrintSelection | null
): { name: string; range?: string }[] {
    const scope = config.scope.sheets
    let chosen: SheetMeta[]
    if (scope === 'all') {
        chosen = sheets
    } else if (scope === 'current') {
        chosen = sheets.filter(s => s.id === currentSheetId)
    } else {
        const idSet = new Set(scope.ids)
        chosen = sheets.filter(s => idSet.has(s.id))
    }
    return chosen.map(s => {
        if (
            config.scope.range === 'selection' &&
            currentSelection != null &&
            currentSelection.sheetId === s.id
        ) {
            return { name: s.name, range: rectToA1(currentSelection.rect) }
        }
        return { name: s.name }
    })
}

// PrintDialog opens from the toolbar's PrintButton. It captures a fresh
// PrintConfig via React Hook Form, calls the server render endpoint
// once per target sheet, joins the fragments under a single print
// envelope, and hands the HTML off to handlePrint. The dialog stays
// open while the print flow runs — on web the print container +
// window.print() happens in front of it; on native the system print
// sheet covers it.
export function PrintDialog({
    isOpen,
    onClose,
    doc,
    driveItemId,
    currentSheetId,
    currentSelection,
}: PrintDialogProps) {
    const sheets = useYSheets(doc)
    const sheetList = useMemo(() => sheets.map(s => ({ id: s.id, name: s.name })), [sheets])
    const selectionAvailable =
        currentSelection != null && currentSelection.sheetId === currentSheetId

    const {
        control,
        handleSubmit,
        setError,
        getValues,
        formState: { errors, isSubmitted, isValid },
    } = useForm<PrintConfig>({
        resolver: zodResolver(printConfigSchema),
        defaultValues: DEFAULT_PRINT_CONFIG,
        mode: 'onChange',
    })

    // useMutation gives us the failure-surface the previous
    // handleSubmit-only path was missing: onError lands in
    // FormErrorSummary via handleMutationErrorsWithForm, and Sentry
    // gets a captureException so silent print failures show up in
    // the error tracker. isPending replaces the previous reliance on
    // formState.isSubmitting (which only tracks form validation, not
    // the post-validation async work).
    const printMutation = useMutation<void, Error, PrintConfig>({
        mutationFn: async (config: PrintConfig) => {
            const targets = resolveTargets(config, sheetList, currentSheetId, currentSelection)
            const fragments: string[] = []
            const source = {
                collectionId: 'drive_items',
                recordId: driveItemId,
                fileName: '',
                displayName: '',
                mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                size: 0,
            }
            for (const target of targets) {
                const { html } = await fetchRenderedHtml(source, {
                    sheet: target.name,
                    range: target.range,
                    scope: 'selection',
                    images: 'embed',
                })
                fragments.push(html)
            }
            const envelope = renderPrintEnvelope(fragments.join(''), buildPrintCss(config))
            // Close the dialog before invoking handlePrint so its focus
            // trap has torn down before #tinycld-print-root is appended
            // to <body> and later removed by the `afterprint` cleanup.
            onClose()
            await handlePrint(envelope)
        },
        onError: err => {
            captureException('calc.print', err)
            handleMutationErrorsWithForm({ setError, getValues })(err)
        },
    })

    const onSubmit = handleSubmit(config => printMutation.mutate(config))

    return (
        <Dialog isOpen={isOpen} onClose={onClose} title="Print" size="xl" testID="print-dialog">
            <Dialog.Body contentClassName="px-5 pb-5 gap-4">
                <FormErrorSummary errors={errors} isEnabled={isSubmitted} />
                <PrintScopeFields
                    control={control}
                    sheets={sheetList}
                    selectionAvailable={selectionAvailable}
                />
                <PrintPageFields control={control} />
                <PrintLayoutFields control={control} />
            </Dialog.Body>
            <Dialog.Footer>
                <Dialog.CancelButton onPress={onClose} />
                <Dialog.ActionButton
                    label="Print"
                    onPress={onSubmit}
                    isDisabled={!isValid || printMutation.isPending}
                />
            </Dialog.Footer>
        </Dialog>
    )
}
